import { type LngLat, type MapPoint, type MapTruck, ZoneMap } from "@majitap/ui";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Truck } from "lucide-react";
import { type BoardRow, fleetQueries, litresOf, type SegmentId, segmentOf, sinceOf } from "../../dispatch";
import { DELIVERY_STATUS } from "../../labels";
import { ageOf, kmBetween, truckLine, truckPositionsQuery, truckState } from "../../tracking";
import { toMapZone, zonesListQuery } from "../../zones";
import styles from "./board.module.css";
import { useFillsitesNear } from "./plan-panel";

type Tone = NonNullable<MapPoint["tone"]>;

/** How a tab colours its deliveries, first match wins, and the legend that says so; the rest is dimmed. */
const KEYS: Record<SegmentId, { label: string; tone: Tone; test: (r: BoardRow) => boolean }[]> = {
  unassigned: [
    { label: "To plan", tone: "warning", test: (r) => segmentOf(r.status) === "unassigned" },
    { label: "Assigned", tone: "info", test: (r) => segmentOf(r.status) === "assigned" },
    { label: "Suspended", tone: "danger", test: (r) => r.status === "suspended" },
    { label: "Under way", tone: "accent", test: (r) => segmentOf(r.status) === "in_progress" },
    { label: "Done", tone: "success", test: (r) => segmentOf(r.status) === "done" },
  ],
  assigned: [
    { label: "Not accepted", tone: "warning", test: (r) => r.status === "assigned" && r.flags.not_accepted },
    { label: "Waiting to start", tone: "info", test: (r) => r.status === "assigned" },
  ],
  in_progress: [
    { label: "Suspended", tone: "danger", test: (r) => r.status === "suspended" },
    { label: "Slow at a step", tone: "warning", test: (r) => segmentOf(r.status) === "in_progress" && r.flags.slow },
    { label: "On the road", tone: "accent", test: (r) => segmentOf(r.status) === "in_progress" },
  ],
  done: [{ label: "Delivered", tone: "success", test: (r) => r.status === "delivered" }],
  closed: [{ label: "Closed", tone: "default", test: (r) => segmentOf(r.status) === "closed" }],
};
const SCOPE: Record<SegmentId, string> = {
  unassigned: "The day's deliveries",
  assigned: "Waiting to start",
  in_progress: "Under way now",
  done: "Delivered",
  closed: "Closed",
};

const lngLat = (p: { latitude?: number | null; longitude?: number | null } | null | undefined): LngLat | null =>
  p?.latitude != null && p.longitude != null ? [p.longitude, p.latitude] : null;

/**
 * The day on a map (`Console / Dispatch / …`, centre): the tab's deliveries coloured by what needs
 * a look and the rest dimmed, the fill sites, the trucks on shift, and the selected delivery with
 * its route. While a load waits for its fill site, lines to the nearest sites with their distance.
 */
export function BoardMap({
  rows,
  segment,
  only,
  zone,
  selected,
  choosingFillsite,
  onSelect,
}: {
  rows: BoardRow[];
  segment: SegmentId;
  /** a Needs attention chip: just these, coloured by their tab */
  only: ((r: BoardRow) => boolean) | null;
  zone: string | null;
  selected: BoardRow | null;
  choosingFillsite: boolean;
  onSelect: (id: string) => void;
}) {
  const zones = useQuery(zonesListQuery);
  const fillsites = useQuery(fleetQueries.fillsites);
  const positions = useQuery(truckPositionsQuery);
  const near = useFillsitesNear(selected?.location ?? null);
  const keys = only ? KEYS.unassigned : KEYS[segment];
  const keyOf = (r: BoardRow) => keys.find((k) => k.test(r));
  const toneOf = (r: BoardRow): Tone => {
    if (only && !only(r)) return "muted";
    return keyOf(r)?.tone ?? "muted";
  };

  const place = lngLat(selected?.location);
  const site = fillsites.data?.find((f) => f.id === selected?.assignment?.fillsite?.id) ?? null;
  const from = lngLat(site);
  const guides = choosingFillsite
    ? near
        .slice(0, 3)
        .map((f) => ({ ...f, at: lngLat(f) }))
        .filter((f): f is typeof f & { at: LngLat } => f.at !== null && f.km !== null)
    : [];

  const deliveries: MapPoint[] = rows.flatMap((r) => {
    const at = lngLat(r.location);
    return at
      ? [
          {
            id: r.id,
            lng: at[0],
            lat: at[1],
            label: r.delivery_number,
            tone: toneOf(r),
            selected: r.id === selected?.id,
          },
        ]
      : [];
  });
  const sites: MapPoint[] = (fillsites.data ?? []).flatMap((f) => {
    const at = lngLat(f);
    const guide = guides.find((g) => g.id === f.id);
    return at
      ? [
          {
            id: `site-${f.id}`,
            lng: at[0],
            lat: at[1],
            label: f.name,
            tone: "site" as const,
            tag: guide ? `${f.name} · ${guide.km} km` : f.name,
          },
        ]
      : [];
  });
  const trucks: MapTruck[] = (positions.data?.data ?? [])
    .filter((p) => p.on_shift)
    .map((p) => ({
      id: p.truck_id,
      lng: p.lng,
      lat: p.lat,
      state: truckState(p),
      title: `${p.plate_number}, ${truckLine(p)}`,
      label:
        p.truck_id === selected?.assignment?.truck?.id
          ? p.plate_number
          : p.stale
            ? `${p.plate_number} · ${ageOf(p.recorded_at)} ago`
            : !p.delivery
              ? `${p.plate_number} · free`
              : undefined,
    }));

  const litres = selected ? litresOf(selected) : 0;
  const km = site && selected ? kmBetween(site, selected.location) : null;
  const headline = !selected
    ? ""
    : selected.status === "suspended"
      ? `Suspended · ${sinceOf(selected.status_since)}`
      : selected.flags.late
        ? "Running late"
        : selected.flags.not_accepted
          ? `Not accepted · ${sinceOf(selected.status_since)}`
          : choosingFillsite
            ? "No fill site yet"
            : DELIVERY_STATUS[selected.status][0];
  const pin =
    selected && place
      ? {
          lng: place[0],
          lat: place[1],
          label: `${selected.delivery_number} · ${headline}`,
          note: [
            selected.order.customer_name,
            litres ? `${litres.toLocaleString("en-KE")} L` : null,
            km !== null && site ? `${km} km from ${site.name}` : null,
          ]
            .filter(Boolean)
            .join(" · "),
          tone: selected.status === "suspended" ? ("danger" as const) : ("default" as const),
        }
      : null;
  const focus: LngLat[] | null = place ? [...(from ? [from] : []), ...guides.map((g) => g.at), place] : null;

  return (
    <ZoneMap
      label="The day's deliveries"
      height="100%"
      zones={(zones.data ?? []).map(toMapZone)}
      points={[...sites, ...deliveries]}
      pointsName="Deliveries and fill sites"
      onSelectPoint={(id) => (id.startsWith("site-") ? undefined : onSelect(id))}
      trucks={trucks}
      selectedTruckId={selected?.assignment?.truck?.id ?? null}
      route={from && place ? [from, place] : null}
      lines={place ? guides.map((g) => [place, g.at]) : []}
      pin={pin}
      focus={focus}
      fitAll
      legend={
        <span className={styles.legend}>
          {keys.map((k) => (
            <span key={k.label} className={styles.key} data-tone={k.tone}>
              {`${k.label} ${rows.filter((r) => keyOf(r) === k && (!only || only(r))).length}`}
            </span>
          ))}
          {segment !== "unassigned" || only ? (
            <span className={styles.key} data-tone="muted">
              Others
            </span>
          ) : null}
          <span className={styles.key} data-tone="site">
            Fill site
          </span>
          <span className={styles.keyTruck}>
            <Truck size={12} aria-hidden /> Truck
          </span>
        </span>
      }
    >
      <span className={styles.scope}>
        <MapPin size={14} aria-hidden /> {`${only ? "Needs attention" : SCOPE[segment]} · ${zone ?? "all zones"}`}
      </span>
    </ZoneMap>
  );
}
